import { useSession } from "@src/appV2/Auth/api/useSession";
import { Platform } from "@src/mobile/utils/platform";
import { pseudo } from "@src/modules/session";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

interface UseSwitchToAdminResult {
  /** True when the admin is currently impersonating a workplace/worker. */
  isImpersonating: boolean;
  /** Dispatches a pseudo action back to the admin user and reloads on web. */
  switchToAdmin: () => void;
}

/**
 * Lightweight alternative to `useAccountSwitcher` for callers that only need
 * the "return to admin view" action. Avoids spinning up the account-search
 * queries and debounced inputs that `useAccountSwitcher` carries.
 */
export function useSwitchToAdmin(): UseSwitchToAdminResult {
  const { admin, adminUserId, userId } = useSession();
  const dispatch = useDispatch();
  const history = useHistory();

  const isImpersonating =
    admin &&
    typeof adminUserId === "string" &&
    typeof userId === "string" &&
    userId !== adminUserId;

  const switchToAdmin = useCallback(() => {
    if (!adminUserId) {
      return;
    }
    localStorage.setItem("selectedUser", adminUserId);
    void dispatch(pseudo({ userId: adminUserId }));
    history.push(getLocation("enter"));
    if (!Platform.isMobile) {
      window.location.reload();
    }
  }, [adminUserId, dispatch, history]);

  return { isImpersonating, switchToAdmin };
}
